import { useParams } from "react-router-dom";
import React, { FC, useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { formatISO, parse } from "date-fns";

import { DatabaseRow } from "@Shape-Digital/kudzu-data/lib/types/common";
import {
  GetCustomerCheckoutFormDataResponsePatient,
  LogInFormPersonType,
} from "@Shape-Digital/kudzu-data/lib/types/actions";

import AppointmentCheckoutPatientForm, {
  PatientFormValues,
} from "../AppointmentCheckoutPatientForm";
import AppointmentCheckoutGuardianForm, {
  GuardianFormValues,
} from "../AppointmentCheckoutGuardianForm";
import AppointmentCheckoutOtherPersonForm, {
  OtherPersonFormValues,
} from "../AppointmentCheckoutOtherPersonForm";
import AppointmentCheckoutLinkedPatientForm from "../AppointmentCheckoutLinkedPatientForm";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";
import { CHECKOUT_DATE_FORMAT } from "../AppointmentCheckoutFormSectionWrapper/getValidationSchema";
import { PatientAgeAlertType } from "../../Patient/PatientAgeAlert";

const getPhoneNumber = (str: string): string => {
  return `+${str.replace(/\D/g, "")}`;
};

const getDateOfBirth = (str: string) =>
  formatISO(parse(str, CHECKOUT_DATE_FORMAT, Date.now()), {
    representation: "date",
  });

type AppointmentCheckoutFormSectionProps = {
  personType: LogInFormPersonType | null;
  onPersonTypeChange: (type: LogInFormPersonType) => void;
  onNotPossibleAnswerClick: (question: string) => void;
  onEmailBlur?: (email: string, valid: boolean) => void | Promise<void>;
  isEmailLoading?: boolean;
  isCheckDataShown?: boolean;
  isEmailInUse?: boolean;
  isCenterUser: boolean;
  selectedCustomer?: DatabaseRow<"customers"> | null;
  prefilledPatient?: GetCustomerCheckoutFormDataResponsePatient | null;
  onSelectCustomer: (customer: DatabaseRow<"customers">) => void;
  ageAlertType: PatientAgeAlertType | null;
  centerName: string;
  centerPhone: string;
  minOnlineBookingAge: number;
  minSelfBookingAge: number;
  onDateBirthChange: (dateBirth: string) => void;
  onDateBirthBlur: (dateBirth: string) => void;
};

const AppointmentCheckoutFormSection: FC<
  AppointmentCheckoutFormSectionProps
> = ({
  personType,
  onPersonTypeChange,
  onNotPossibleAnswerClick,
  onEmailBlur,
  isEmailLoading,
  isCheckDataShown,
  isEmailInUse,
  isCenterUser,
  selectedCustomer,
  prefilledPatient,
  onSelectCustomer,
  ageAlertType,
  centerName,
  centerPhone,
  minOnlineBookingAge,
  minSelfBookingAge,
  onDateBirthBlur,
  onDateBirthChange,
}) => {
  const { appointmentId } = useParams();

  const { values, isSubmitting } = useFormikContext();

  const appointmentCaptureCartData = useFirebaseAppFunction(
    "appointmentCaptureCartData",
  );

  const valuesRef = useRef(values);
  valuesRef.current = values;

  const isSubmittingRef = useRef(isSubmitting);
  isSubmittingRef.current = isSubmitting;

  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  // Clicking submit blurs the last field, which schedules a cart capture that
  // would race with saveAppointment/appointmentCreatePayment — drop it.
  useEffect(() => {
    if (isSubmitting && blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  }, [isSubmitting]);

  if (!personType || !appointmentId) return null;

  // For patient/legalGuardian/familyMember: show the linked patient select form when
  // the customer is authenticated (isCheckDataShown) or staff has selected a customer.
  const isLoggedInFlow =
    personType !== "otherPerson" && (isCheckDataShown || !!selectedCustomer);

  if (isLoggedInFlow) {
    return (
      <AppointmentCheckoutLinkedPatientForm
        personType={personType}
        onPersonTypeChange={onPersonTypeChange}
        selectedCustomer={selectedCustomer}
        prefilledPatient={prefilledPatient}
        ageAlertType={ageAlertType}
        centerName={centerName}
        centerPhone={centerPhone}
        minOnlineBookingAge={minOnlineBookingAge}
        minSelfBookingAge={minSelfBookingAge}
        onDateBirthBlur={onDateBirthBlur}
        onNotPossibleAnswerClick={onNotPossibleAnswerClick}
        onEmailBlur={onEmailBlur}
        isEmailLoading={isEmailLoading}
        isEmailInUse={isEmailInUse}
      />
    );
  }

  const onBlur = () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);

    blurTimeoutRef.current = setTimeout(() => {
      const currentValues = valuesRef.current;

      if (!appointmentId || isSubmittingRef.current) return;

      switch (personType) {
        case "patient": {
          const patientFormValues = currentValues as PatientFormValues;

          appointmentCaptureCartData({
            appointmentId,
            creatorType: "patient",
            email: patientFormValues.email,
            firstName: patientFormValues.firstName,
            lastName: patientFormValues.lastName,
            dateOfBirth: patientFormValues.dateOfBirth
              ? getDateOfBirth(patientFormValues.dateOfBirth)
              : "",
            phoneNumber: patientFormValues.phoneNumber
              ? getPhoneNumber(patientFormValues.phoneNumber)
              : "",
          });
          break;
        }
        case "legalGuardian": {
          const guardianFormValues = currentValues as GuardianFormValues;

          appointmentCaptureCartData({
            appointmentId,
            creatorType: "legalGuardian",
            guardianEmail: guardianFormValues.guardianEmail,
            guardianFirstName: guardianFormValues.guardianFirstName,
            guardianLastName: guardianFormValues.guardianLastName,
            firstName: guardianFormValues.firstName,
            lastName: guardianFormValues.lastName,
            dateOfBirth: guardianFormValues.dateOfBirth
              ? getDateOfBirth(guardianFormValues.dateOfBirth)
              : "",
            guardianPhoneNumber: guardianFormValues.guardianPhoneNumber
              ? getPhoneNumber(guardianFormValues.guardianPhoneNumber)
              : "",
          });
          break;
        }
        case "familyMember": {
          const familyFormValues = currentValues as GuardianFormValues;

          appointmentCaptureCartData({
            appointmentId,
            creatorType: "familyMember",
            familyMemberEmail: familyFormValues.guardianEmail,
            familyMemberFirstName: familyFormValues.guardianFirstName,
            familyMemberLastName: familyFormValues.guardianLastName,
            firstName: familyFormValues.firstName,
            lastName: familyFormValues.lastName,
            dateOfBirth: familyFormValues.dateOfBirth
              ? getDateOfBirth(familyFormValues.dateOfBirth)
              : "",
            familyMemberPhoneNumber: familyFormValues.guardianPhoneNumber
              ? getPhoneNumber(familyFormValues.guardianPhoneNumber)
              : "",
          });
          break;
        }
        case "otherPerson": {
          const otherPersonFormValues = currentValues as OtherPersonFormValues;

          appointmentCaptureCartData({
            appointmentId,
            creatorType: "otherPerson",
            email: otherPersonFormValues.email,
            otherPersonEmail: otherPersonFormValues.otherPersonEmail,
            firstName: otherPersonFormValues.firstName,
            lastName: otherPersonFormValues.lastName,
            otherPersonFirstName: otherPersonFormValues.otherPersonFirstName,
            otherPersonLastName: otherPersonFormValues.otherPersonLastName,
            dateOfBirth: otherPersonFormValues.dateOfBirth
              ? getDateOfBirth(otherPersonFormValues.dateOfBirth)
              : "",
            phoneNumber: otherPersonFormValues.phoneNumber
              ? getPhoneNumber(otherPersonFormValues.phoneNumber)
              : "",
          });
          break;
        }
        default:
          break;
      }
    }, 300);
  };

  const nonLoggedInComponents = {
    patient: AppointmentCheckoutPatientForm,
    legalGuardian: AppointmentCheckoutGuardianForm,
    familyMember: AppointmentCheckoutGuardianForm,
    otherPerson: AppointmentCheckoutOtherPersonForm,
  };

  const FormComponent = nonLoggedInComponents[personType];

  return (
    <FormComponent
      onNotPossibleAnswerClick={onNotPossibleAnswerClick}
      onEmailBlur={onEmailBlur}
      isEmailLoading={isEmailLoading}
      isCheckDataShown={isCheckDataShown}
      isEmailInUse={isEmailInUse}
      isCenterUser={isCenterUser}
      onSelectCustomer={onSelectCustomer}
      onBlur={onBlur}
      ageAlertType={ageAlertType}
      centerName={centerName}
      centerPhone={centerPhone}
      minOnlineBookingAge={minOnlineBookingAge}
      minSelfBookingAge={minSelfBookingAge}
      onDateBirthBlur={onDateBirthBlur}
      onDateBirthChange={onDateBirthChange}
    />
  );
};

export default AppointmentCheckoutFormSection;
